import { useNavigate } from 'react-router-dom';
import { Store, Wrench, Warehouse, Building2, Truck, AlertTriangle, Info, Shield, Landmark } from 'lucide-react';
import { useCityContext } from './city/useCityContext';
import CityScreen from './city/CityScreen';

function formatRemainingSeconds(seconds) {
  const total = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  if (minutes > 0) {
    return `${minutes}m ${String(secs).padStart(2, '0')}s`;
  }
  return `${secs}s`;
}

const MODULES = [
  { key: 'shop', title: 'Tienda', subtitle: 'Compra y venta de equipo', icon: Store, to: '/city/shop' },
  { key: 'workshop', title: 'Taller', subtitle: 'Reparacion rapida de tu BOT', icon: Wrench, to: '/city/workshop' },
  { key: 'hangar', title: 'Hangar', subtitle: 'Gestión operativa de unidades', icon: Building2, to: '/city/hangar' },
  { key: 'warehouse', title: 'Almacén', subtitle: 'Depósito y logística', icon: Warehouse, to: '/city/warehouse' },
  { key: 'transport', title: 'Transporte', subtitle: 'Rutas entre sectores/ciudades', icon: Truck, to: '/city/transport' },
  { key: 'rpg', title: 'Cúpula', subtitle: 'Zona urbana', icon: Landmark, to: '/rpg' }
];

export default function CityHub() {
  const navigate = useNavigate();
  const { loading, error, context, cityTypeLabel } = useCityContext();
  const rpgEnabled = Boolean(context.rpg?.available || context.rpg?.hasConfig || context.rpg?.hasMap);

  if (loading) {
    return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Sincronizando modulo de ciudad...</div>;
  }

  if (error) {
    return <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">{error}</div>;
  }

  const isInCity = Boolean(context.city);
  const access = context.access || {};
  const transportActive = Boolean(context.transport);
  const transportRemaining = Number(context.transport?.seconds_remaining || 0);
  const durability = [
    Number(context.bot?.v_head || 0),
    Number(context.bot?.v_torso || 0),
    Number(context.bot?.v_arm_l || 0),
    Number(context.bot?.v_arm_r || 0),
    Number(context.bot?.v_base_l || 0),
    Number(context.bot?.v_base_r || 0),
    Number(context.bot?.v_leg_l || 0),
    Number(context.bot?.v_leg_r || 0)
  ].reduce((a, b) => a + b, 0);

  function isModuleDisabled(moduleKey) {
    if (!isInCity) return true;
    if (context.transport && moduleKey !== 'transport') return true;
    if (moduleKey === 'rpg') return context.transport ? true : !rpgEnabled;
    if (moduleKey === 'transport') return access.canUseTransport === false;
    if (moduleKey === 'hangar') return access.canUseHangar === false;
    if (moduleKey === 'warehouse') return access.canUseWarehouse === false;
    return false;
  }

  function getDisabledReason(moduleKey) {
    if (!isInCity) return 'Debes estar en una ciudad.';
    if (context.transport && moduleKey !== 'transport') return 'El BOT está en tránsito aéreo.';
    if (moduleKey === 'rpg') return 'Esta ciudad no tiene Cúpula activa.';
    if (moduleKey === 'transport' && access.canUseTransport === false) return 'Solo hay vuelos desde ciudades y capitales.';
    if (moduleKey === 'hangar' && access.canUseHangar === false) return 'Acceso restringido para piloto neutral en ciudad de clan.';
    if (moduleKey === 'warehouse' && access.canUseWarehouse === false) return 'Acceso restringido para piloto neutral en ciudad de clan.';
    return '';
  }

  return (
    <CityScreen
      context={context}
      cityTypeLabel={cityTypeLabel}
      title={context.city?.name || 'NODO URBANO'}
      subtitle="Centro de servicios urbanos"
      showBackToCity={false}
    >
      {transportActive && (
        <section
          className="relative overflow-hidden rounded-2xl border border-red-400/40 bg-[#05070f] shadow-[0_0_40px_rgba(255,40,40,0.18)]"
          style={{
            backgroundImage: 'url(/img/air-travel-banner.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-[#01040a]/88" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#01040a]/96 via-[#01040a]/82 to-[#01040a]/92" />
          <div className="relative z-10 grid gap-4 lg:grid-cols-[1.2fr_0.8fr] p-5 md:p-6">
            <div className="space-y-3 max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-200/50 bg-red-950/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.35em] text-red-50 shadow-[0_0_16px_rgba(0,0,0,0.35)]">
                BOT EN TRÁNSITO
              </div>
              <h2 className="text-3xl md:text-4xl font-black tracking-wide text-[#fff1f1] drop-shadow-[0_0_14px_rgba(0,0,0,0.85)]">
                Ciudad bloqueada por vuelo aéreo
              </h2>
              <p className="text-sm md:text-base text-slate-100 max-w-2xl drop-shadow-[0_0_10px_rgba(0,0,0,0.9)]">
                El BOT está viajando entre ciudades. Mientras dure el trayecto, la ciudad queda cerrada para tienda, taller, hangar, almacén y terrenos. Solo la pantalla de transporte sigue operativa.
              </p>
              <div className="flex flex-wrap gap-2 text-xs md:text-sm">
                <span className="rounded-full border border-red-200/40 bg-black/45 px-3 py-1 text-red-50 shadow-[0_0_12px_rgba(0,0,0,0.35)]">
                  Destino: {context.transport?.destination_city_name || 'Desconocido'}
                </span>
                <span className="rounded-full border border-red-200/40 bg-black/45 px-3 py-1 text-red-50 shadow-[0_0_12px_rgba(0,0,0,0.35)]">
                  Restante: {formatRemainingSeconds(transportRemaining)}
                </span>
                <span className="rounded-full border border-red-200/40 bg-black/45 px-3 py-1 text-red-50 shadow-[0_0_12px_rgba(0,0,0,0.35)]">
                  Estado: {context.transport?.status || 'in_transit'}
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-red-200/25 bg-[#01040a]/88 p-4 backdrop-blur-sm shadow-[0_0_28px_rgba(0,0,0,0.45)]">
              <p className="text-[11px] uppercase tracking-[0.4em] text-red-100/80">Acciones bloqueadas</p>
              <div className="mt-3 space-y-2 text-sm text-slate-100/90">
                <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-600/60 bg-black/50 px-3 py-2">
                  <span>Tienda</span>
                  <span className="text-red-100 font-bold">Bloqueada</span>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-600/60 bg-black/50 px-3 py-2">
                  <span>Taller</span>
                  <span className="text-red-100 font-bold">Bloqueado</span>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-600/60 bg-black/50 px-3 py-2">
                  <span>Hangar</span>
                  <span className="text-red-100 font-bold">Bloqueado</span>
                </div>
                <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-600/60 bg-black/50 px-3 py-2">
                  <span>Almacén</span>
                  <span className="text-red-100 font-bold">Bloqueado</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {!isInCity && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5" />
          Tu BOT no está en una ciudad. Muévete a una coordenada urbana para usar módulos.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {MODULES.map((module) => {
          const Icon = module.icon;
          const disabled = isModuleDisabled(module.key);
          return (
            <button
              key={module.key}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && navigate(module.to)}
              className={`text-left rounded-xl border p-4 transition-all ${
                disabled
                  ? 'opacity-60 cursor-not-allowed border-slate-700 bg-[#0a1020]'
                  : 'border-cyan-400/50 bg-[#091427] hover:border-cyan-300'
              }`}
            >
              <Icon size={22} className="text-cyan-300 mb-2" />
              <h3 className="font-bold text-cyan-200">{module.title}</h3>
              <p className="text-sm text-slate-300/80">{module.subtitle}</p>
              {disabled && <p className="text-[11px] text-amber-300 mt-2">{getDisabledReason(module.key)}</p>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="font-bold text-cyan-300 mb-2 flex items-center gap-2"><Shield size={16} /> Estado del BOT</h2>
          <p className="text-sm text-slate-300">Unidad: <span className="text-cyan-200 font-bold">{context.bot?.name || 'N/A'}</span></p>
          <p className="text-sm text-slate-300">Nivel: <span className="text-cyan-200 font-bold">{context.bot?.level || 1}</span></p>
          <p className="text-sm text-slate-300">Experiencia: <span className="text-cyan-200 font-bold">{context.bot?.experience || 0}</span></p>
          <p className="text-sm text-slate-300">Integridad total: <span className="text-cyan-200 font-bold">{durability}</span></p>
        </section>

        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="font-bold text-cyan-300 mb-2">Economía local</h2>
          <p className="text-sm text-slate-300">Oro disponible: <span className="text-cyan-200 font-bold">{context.bot?.money || 0}</span></p>
          <p className="text-xs text-slate-400 mt-2">Consejo: reserva oro para munición, kits médicos y reparaciones del taller.</p>
        </section>

        <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
          <h2 className="font-bold text-cyan-300 mb-2 flex items-center gap-2"><Info size={16} /> Intel urbana</h2>
          <p className="text-xs text-slate-300">1. Tienda: arma + munición + kit para salir preparado.</p>
          <p className="text-xs text-slate-300">2. Taller: repara el BOT antes de cada salida táctica.</p>
          <p className="text-xs text-slate-300">3. Transporte: planifica rutas seguras entre sectores.</p>
        </section>
      </div>
    </CityScreen>
  );
}
